import React, { useEffect, useState } from 'react';
import ReactSlider from 'react-slider';
import { formatNumbers } from '../../utils/convert-to-arabic-price';
import { useDebouncedCallback } from 'use-debounce';
import { getDocumentDirection } from '../../utils/get-document-direction';
import RIYALIcon from '../../assets/riyal.svg?react';

export type FilterType =
  | 'gender'
  | 'brand'
  | 'length'
  | 'price'
  | 'color'
  | 'size'
  | 'duration'
  | 'compatible_devices'
  | 'storage'
  | 'ram'
  | 'power'
  | 'port_type'
  | 'material'
  | 'style'
  | 'categories';

interface ComponentBuilderProps {
  filters: {
    [key in FilterType]?: {
      options?: { value: string; count: number }[];
      min?: number;
      max?: number;
      step?: number;
      value: string[] | { from: number; to: number };
    };
  };
  onChange: (
    type: FilterType,
    value: string[] | { from: number; to: number },
  ) => void;
  primaryColor?: string;
  countColor?: string;
  minInitialPrice?: number | null;
  maxInitialPrice?: number | null;
  appliedFilters?: Record<string, string[] | { from: number; to: number }>;
  currency?: string;
  language?: string;
  locale?: string;
}

const FILTER_TITLES_AR: Record<FilterType, string> = {
  gender: 'النوع',
  brand: 'العلامة التجارية',
  size: window.location.hostname === 'laverne.com' ? 'الحجم' : 'المقاس',
  price: 'السعر',
  color: 'اللون',
  length: 'الطول',
  duration: 'المدة',
  compatible_devices: 'الأجهزة المتوافقة',
  storage: 'سعة التخزين',
  ram: 'الذاكرة',
  power: 'القدرة',
  port_type: 'نوع المنفذ',
  material: 'المادة',
  style: 'النمط',
  categories: 'الفئات',
};

const FILTER_TITLES_EN: Record<FilterType, string> = {
  gender: 'Gender',
  brand: 'Brand',
  size: 'Size',
  price: 'Price',
  color: 'Color',
  length: 'Length',
  duration: 'Duration',
  compatible_devices: 'Compatible Devices',
  storage: 'Storage',
  ram: 'RAM',
  power: 'Power',
  port_type: 'Port Type',
  material: 'Material',
  categories: 'Categories',
  style: 'Style',
};

const GENDER_OPTIONS_AR: Record<string, string> = {
  male: 'رجالي',
  female: 'نسائي',
};

const FiltersComponentBuilder: React.FC<ComponentBuilderProps> = ({
  filters,
  onChange,
  primaryColor,
  // countColor,
  minInitialPrice,
  maxInitialPrice,
  appliedFilters,
  currency = 'SAR',
  language,
  locale = 'ar',
}) => {
  const availableFilterTypes = Object.keys(filters);
  const [activeTab, setActiveTab] = useState<string>(availableFilterTypes[0]);

  // Add local state for price values
  const [localPriceValue, setLocalPriceValue] = useState<{
    from: number;
    to: number;
  } | null>(null);

  // Create debounced onChange handler
  const debouncedPriceChange = useDebouncedCallback(
    (type: FilterType, value: { from: number; to: number }) => {
      onChange(type, value);
    },
    500, // 300ms delay
  );

  const [showAllTags, setShowAllTags] = useState<Record<string, boolean>>({});

  const renderFilterTitle = (filterType: string) => {
    const shouldShowPriceDot = () => {
      if (!localPriceValue || filterType !== 'price') return false;

      return (
        formatNumbers(
          localPriceValue.from || 0,
          language as 'ar-SA' | 'en-US',
        ) !==
          formatNumbers(minInitialPrice || 0, language as 'ar-SA' | 'en-US') ||
        formatNumbers(
          localPriceValue.to || 0,
          language as 'ar-SA' | 'en-US',
        ) !== formatNumbers(maxInitialPrice || 0, language as 'ar-SA' | 'en-US')
      );
    };

    return (
      <div
        className="filter-header"
        onClick={() => setActiveTab(filterType)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: primaryColor,
          padding: '10px 10px',
          transition: 'all 0.3s ease',
          position: 'relative',
        }}
      >
        {activeTab === filterType && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              height: '2px',
              background: primaryColor,
              left: 0,
              right: 0,
              borderRadius: '8px 8px 0 0',
            }}
          />
        )}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className="filter-title" style={{ marginInlineEnd: '5px' }}>
            {locale === 'ar'
              ? FILTER_TITLES_AR[filterType as FilterType]
              : FILTER_TITLES_EN[filterType as FilterType] || filterType}
          </div>
          {filterType === 'price'
            ? shouldShowPriceDot() && (
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: primaryColor,
                    marginInlineStart: '5px',
                  }}
                />
              )
            : (filters[filterType as FilterType]?.value as string[]).length >
                0 && (
                <div
                  style={{
                    color: 'white',
                    width: '20px',
                    height: '23px',
                    borderRadius: '4px',
                    backgroundColor: primaryColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {
                    (filters[filterType as FilterType]?.value as string[])
                      .length
                  }
                </div>
              )}
        </div>
      </div>
    );
  };

  const renderContent = (type: string) => {
    const filterData = filters[type as FilterType];
    if (!filterData) return null;

    if (type === 'price') {
      const { min = 0, max = 1000, value } = filterData;
      const priceValue = value as { from: number; to: number };
      const currentValue = localPriceValue || priceValue;

      // Check if min or max have decimal places
      const hasDecimals = (num: number) => num % 1 !== 0;
      const shouldUseDecimals = hasDecimals(min) || hasDecimals(max);
      const step = shouldUseDecimals ? 0.01 : 1;

      return (
        <div className="price-slider">
          <ReactSlider
            className="horizontal-slider"
            invert={getDocumentDirection() === 'rtl'}
            thumbClassName="alfinder-thumb"
            trackClassName="track"
            renderTrack={(props, state) => {
              if (state.index === 1) {
                return (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      background: primaryColor || '#0056b3',
                    }}
                  />
                );
              }
              return <div {...props} />;
            }}
            min={min}
            max={max}
            step={step}
            value={[currentValue.from, currentValue.to]}
            onChange={(newRange) => {
              const newValue = shouldUseDecimals
                ? {
                    from: Number(newRange[0].toFixed(2)),
                    to: Number(newRange[1].toFixed(2)),
                  }
                : {
                    from: Math.round(newRange[0]),
                    to: Math.round(newRange[1]),
                  };
              setLocalPriceValue(newValue);
              debouncedPriceChange(type, newValue);
            }}
            renderThumb={(props, state) => (
              <div {...props}>
                <span
                  className="price-label"
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  {formatNumbers(
                    shouldUseDecimals
                      ? Number(state.valueNow.toFixed(2))
                      : Math.round(state.valueNow),
                    (language as 'ar-SA') || 'en-US',
                    currency,
                  )}
                  {currency === 'SAR' && <RIYALIcon />}
                </span>
              </div>
            )}
          />
        </div>
      );
    }

    const options = filterData.options || [];
    const isShowingAll = showAllTags[type] || false;
    const displayOptions = isShowingAll ? options : options.slice(0, 14);
    const hasMoreOptions = options.length > 14;

    return (
      <div className="tags-container">
        {displayOptions.map((option: { value: string; count: number }) => (
          <span
            key={option.value}
            onClick={() => {
              const currentValues = filterData.value as string[];
              const updatedValues = currentValues.includes(option.value)
                ? currentValues.filter((v) => v !== option.value)
                : [...currentValues, option.value];
              onChange(type as FilterType, updatedValues);
            }}
            style={{
              display: 'inline-block',
              padding: '5px 10px',
              margin: '5px',
              borderRadius: '15px',
              cursor: 'pointer',
              backgroundColor: (filterData.value as string[]).includes(
                option.value,
              )
                ? primaryColor || '#0056b3'
                : '#f0f0f0',
              color: (filterData.value as string[]).includes(option.value)
                ? 'white'
                : 'black',
              transition: 'all 0.3s ease',
            }}
          >
            {type === 'gender' ? GENDER_OPTIONS_AR[option.value] : option.value}
          </span>
        ))}
        {hasMoreOptions && (
          <button
            onClick={() => {
              setShowAllTags((prev) => ({
                ...prev,
                [type]: !isShowingAll,
              }));
            }}
            style={{
              background: 'none',
              border: 'none',
              padding: '5px 10px',
              cursor: 'pointer',
              color: primaryColor,
              fontWeight: 'bold',
              fontSize: '12px',
              position: 'unset',
            }}
          >
            {isShowingAll
              ? locale === 'ar'
                ? 'عرض أقل'
                : 'Show Less'
              : locale === 'ar'
              ? 'عرض المزيد'
              : 'Show More'}
          </button>
        )}
      </div>
    );
  };

  // Only render if there are filters
  if (availableFilterTypes.length === 0) {
    return null;
  }

  useEffect(() => {
    if (appliedFilters && Object.keys(appliedFilters).length === 0) {
      setLocalPriceValue(null);
    }
  }, [appliedFilters]);

  return (
    <div className="component-builder">
      <div className="tabs-container">
        {availableFilterTypes.map((filterType) => (
          <div key={filterType}>{renderFilterTitle(filterType)}</div>
        ))}
      </div>
      <div style={{ marginTop: '10px' }}>{renderContent(activeTab)}</div>
    </div>
  );
};

export default FiltersComponentBuilder;
